/**
 * パーセンテージ計算機能
 *
 * Row %, Column %, Total % を計算する
 */

import type { CellValue, RowKey, ColumnKey, AggregationFunction } from './types';

/**
 * 指定された集約関数でパーセンテージ表示が有効かチェック
 */
function isPercentageValidForAggregation(aggregation: AggregationFunction): boolean {
  return aggregation === 'sum' || aggregation === 'count';
}

/**
 * 行パーセンテージを計算
 *
 * @param cellValue セル値
 * @param rowTotal 行合計
 * @param aggregations 各値フィールドの集約関数
 * @returns パーセンテージ配列（各値フィールドに対応、AVG/MIN/MAXはundefined）
 */
export function calculateRowPercentages(
  cellValue: CellValue,
  rowTotal: CellValue | undefined,
  aggregations: AggregationFunction[]
): (number | undefined)[] | undefined {
  if (!rowTotal || cellValue.values.length === 0) {
    return undefined;
  }

  return cellValue.values.map((value, index) => {
    // AVG/MIN/MAXの場合はundefinedを返す
    if (!isPercentageValidForAggregation(aggregations[index])) {
      return undefined;
    }

    const total = rowTotal.values[index];
    if (total === 0 || total === null || total === undefined) {
      return 0;
    }
    return (value / total) * 100;
  });
}

/**
 * 列パーセンテージを計算
 *
 * @param cellValue セル値
 * @param columnTotal 列合計
 * @param aggregations 各値フィールドの集約関数
 * @returns パーセンテージ配列（各値フィールドに対応、AVG/MIN/MAXはundefined）
 */
export function calculateColumnPercentages(
  cellValue: CellValue,
  columnTotal: CellValue | undefined,
  aggregations: AggregationFunction[]
): (number | undefined)[] | undefined {
  if (!columnTotal || cellValue.values.length === 0) {
    return undefined;
  }

  return cellValue.values.map((value, index) => {
    // AVG/MIN/MAXの場合はundefinedを返す
    if (!isPercentageValidForAggregation(aggregations[index])) {
      return undefined;
    }

    const total = columnTotal.values[index];
    if (total === 0 || total === null || total === undefined) {
      return 0;
    }
    return (value / total) * 100;
  });
}

/**
 * 総計パーセンテージを計算
 *
 * @param cellValue セル値
 * @param grandTotal 総計
 * @param aggregations 各値フィールドの集約関数
 * @returns パーセンテージ配列（各値フィールドに対応、AVG/MIN/MAXはundefined）
 */
export function calculateTotalPercentages(
  cellValue: CellValue,
  grandTotal: CellValue | undefined,
  aggregations: AggregationFunction[]
): (number | undefined)[] | undefined {
  if (!grandTotal || cellValue.values.length === 0) {
    return undefined;
  }

  return cellValue.values.map((value, index) => {
    // AVG/MIN/MAXの場合はundefinedを返す
    if (!isPercentageValidForAggregation(aggregations[index])) {
      return undefined;
    }

    const total = grandTotal.values[index];
    if (total === 0 || total === null || total === undefined) {
      return 0;
    }
    return (value / total) * 100;
  });
}

/**
 * パーセンテージを全て計算してセル値に追加
 *
 * @param cells セルデータ（Map）
 * @param rowTotals 行合計（Map）
 * @param columnTotals 列合計（Map）
 * @param grandTotal 総計
 * @param percentageMode パーセンテージモード
 * @param aggregations 各値フィールドの集約関数
 */
export function calculateAllPercentages(
  cells: Map<string, CellValue>,
  rowTotals: Map<RowKey, CellValue> | undefined,
  columnTotals: Map<ColumnKey, CellValue> | undefined,
  grandTotal: CellValue | undefined,
  percentageMode: 'none' | 'row' | 'column' | 'total' | 'all',
  aggregations: AggregationFunction[]
): void {
  if (percentageMode === 'none') {
    return;
  }

  // 各セルに対してパーセンテージを計算
  for (const [cellKey, cellValue] of cells.entries()) {
    // セルキーから行キーと列キーを抽出
    const match = cellKey.match(/^row:(.*):col:(.*)$/);
    if (!match) continue;

    const rowKey = match[1];
    const colKey = match[2];

    cellValue.percentages = {};

    if (percentageMode === 'row' || percentageMode === 'all') {
      cellValue.percentages.row = calculateRowPercentages(
        cellValue,
        rowTotals?.get(rowKey),
        aggregations
      );
    }

    if (percentageMode === 'column' || percentageMode === 'all') {
      cellValue.percentages.column = calculateColumnPercentages(
        cellValue,
        columnTotals?.get(colKey),
        aggregations
      );
    }

    if (percentageMode === 'total' || percentageMode === 'all') {
      cellValue.percentages.total = calculateTotalPercentages(
        cellValue,
        grandTotal,
        aggregations
      );
    }
  }
}
